import { put } from 'redux-saga/effects';
import { push } from 'redux-first-history'
import { doStartTerminalActivation } from 'Route/Homepage/modules/HomepageActions'
import componentConfig from 'Route/CompanyData/modules/componentConfig'

/**
 * Summary. Deep Linking
 *
 * Description. Structured object to handling the deeplinking inside the portal. This object has a map that contains all the possible deep linking url,
 * and some method used by the application to check the correct flow.
 * Every sub object of the routes has an action method which redirects the user to the url of the DL.
 *
 * @since 1.0.0
 * @access private
 *
 *
 * @name deepLinkService
 * @param {object} routes - The routes that describe all possible deep linking url
 */

const buildRouteWithParams = (baseRoute, otherParams) => {
  if (otherParams && Object.keys(otherParams).length > 0) {
    return `${baseRoute}?${new URLSearchParams(otherParams).toString()}`;
  }
  return baseRoute;
};

const terminalActivationPaths = {
  '/profile/company-data': 'TERMINAL_ACTIVATION',
  '/profile/company-data/detail': 'TERMINAL_ACTIVATION',
  '/profile/company-data/detailterminal': 'TERMINAL_ACTIVATION'
}

const isTerminalActivationAvailable = () => componentConfig.companyData.terminalActivation.isVisible()

export const parseDeepLink = (value) => {
  if (typeof value !== 'string') return { deepLink: '', otherParams: {} }

  const separatorIndex = value.indexOf('?')
  const deepLink = separatorIndex === -1 ? value : value.slice(0, separatorIndex)
  const searchParams = new URLSearchParams(separatorIndex === -1 ? '' : value.slice(separatorIndex + 1))
  const otherParams = {}

  searchParams.forEach((parameterValue, key) => {
    if (key !== 'deeplink') otherParams[key] = parameterValue
  })

  const deepLinkValue = searchParams.get('deeplink') || deepLink
  const normalizedPath = deepLinkValue.replace(/\/+$/, '') || '/'
  const terminalActivationDeepLink = isTerminalActivationAvailable() && terminalActivationPaths[normalizedPath]

  return {
    deepLink: terminalActivationDeepLink || '',
    otherParams: terminalActivationDeepLink ? otherParams : {}
  }
}

const deepLinkService = {
  routes: {
    results: {
      depth: 1,
      path: '/results',
      action: function* go() {
        yield put(push('/results'));
      }
    },
    xpay_360: {
      depth: 1,
      path: '/services/xpay',
      action: function* go() {
        yield put(push('/services/xpay'));
      }
    },
    xpay360_gen: {
      depth: 1,
      path: '/services/xpay',
      action: function* go() {
        yield put(push('/services/xpay'));
      }
    },
    xpay360_hotel: {
      depth: 1,
      path: '/services/xpay-hotel',
      action: function* go() {
        yield put(push('/services/xpay-hotel'));
      }
    },
    pblplus_rist: {
      depth: 1,
      path: '/services/xpay-ristorante',
      action: function* go() {
        yield put(push('/services/xpay-ristorante'));
      }
    },
    micropayments: {
      depth: 1,
      path: '/services/micropagamenti',
      action: function* go() {
        yield put(push('/services/micropagamenti'));
      }
    },
    backup_pos: {
      depth: 1,
      path: '/backup-pos',
      action: function* go() {
        yield put(push('/backup-pos'));
      }
    },
    amex: {
      depth: 1,
      path: '/services/amex',
      action: function* go() {
        yield put(push('/services/amex'));
      }
    },
    amex_lodging: {
      depth: 1,
      path: '/services/amex',
      action: function* go() {
        yield put(push('/services/amex'));
      }
    },
    // tutti_pagamenti: {
    //   depth: 1,
    //   action: function* go() {
    //     yield put(push('/services/Dcc'));
    //   }
    // },
    buonopasto: {
      depth: 1,
      path: '/services/buoni-pasto',
      action: function* go() {
        yield put(push('/services/buoni-pasto'));
      }
    },
    // vendi_mobilita: {
    //   depth: 1,
    //   action: function* go() {
    //     yield put(push('/')); // TBD - check if this is needed
    //   }
    // },
    homepage: {
      depth: 1,
      path: '/',
      action: function* go() {
        yield put(push('/'));
      }
    },
    mobile_pos: {
      depth: 1,
      path: '/',
      action: function* go() {
        yield put(push('/')); // TBD - check if this is needed
      }
    },
    pos_addition: {
      depth: 1,
      path: '/services/add-pos/store-selection',
      action: function* go() {
        yield put(push('/services/add-pos/store-selection'));
      }
    },
    pay_by_link: {
      depth: 1,
      path: '/services/pay-by-link/store-selection',
      action: function* go() {
        yield put(push('/services/pay-by-link/store-selection'));
      }
    },
    smartpos: { // change-pos
      depth: 1,
      path: '/services/change-pos/terminal-selection',
      action: function* go() {
        yield put(push('/services/change-pos/terminal-selection'));
      }
    },
    company_data: {
      depth: 1,
      path: '/profile/company-data',
      action: function* go() {
        yield put(push('/profile/company-data'));
      }
    },
    terminal_activation: {
      depth: 1,
      action: function* go(otherParams) {
        const payload = isTerminalActivationAvailable()
          ? { fromDeepLink: true, ...otherParams }
          : {}
        yield put(doStartTerminalActivation(payload))
      }
    },
    documents: {
      depth: 1,
      path: '/documents',
      action: function* go() {
        yield put(push('/documents'));
      }
    },
    reports: {
      depth: 1,
      action: function* go() {
        yield put(push('/report?tab=history'));
      }
    },
    cash_out: {
      depth: 1,
      path: '/pay-by-link',
      action: function* go() {
        yield put(push('/pay-by-link'));
      }
    },
    pci: {
      depth: 1,
      path: '/profile/company-data',
      action: function* go() {
        yield put(push('/profile/company-data'));
      }
    },
    faq: {
      depth: 1,
      path: '/support',
      action: function* go() {
        yield put(push('/support'));
      }
    },
    offers_services: {
      depth: 1,
      path: '/services/categories',
      action: function* go() {
        yield put(push('/services/categories'));
      }
    },
    communications: {
      depth: 1,
      path: '/profile/comunications',
      action: function* go() {
        yield put(push('/profile/comunications'));
      }
    },
    exclusive: {
      depth: 1,
      path: '/services/nexi-exclusive',
      action: function* go() {
        yield put(push('/services/nexi-exclusive'));
      }
    },
    vet_dig_new: {
      depth: 1,
      path: '/services/vetrina-digitale',
      action: function* go() {
        yield put(push('/services/vetrina-digitale'));
      }
    },
    dcc: {
      depth: 1,
      path: '/services/Dcc',
      action: function* go() {
        yield put(push('/services/Dcc'));
      }
    },
    isp_pos: {
      depth: 1,
      path: '/softpos',
      action: function* go() {
        yield put(push('/softpos'));
      }
    },
    pagolight: {
      depth: 1,
      path: '/services/heylight',
      action: function* go() {
        yield put(push('/services/heylight'));
      }
    },
    switch_conv: {
      depth: 1,
      path: '/services/rubicone',
      action: function* go() {
        yield put(push('/services/rubicone')); // add navigate('/services/rubicone', { state: { promoType } })
      }
    },
    loyalty: {
      depth: 1,
      path: '/nexiclub',
      resolver: 'loyalty',
      action: function* go() {
        yield put(push('/nexiclub')); // Ex Engagement
      }
    },
    smartpos_api: {
      depth: 1,
      path: '/api-cloud/activation',
      action: function* go(otherParams) {
        const route = buildRouteWithParams('/api-cloud/activation', otherParams);
        yield put(push(route));
      }
    },
    cashback: {
      depth: 1,
      path: '/services/cashback-info',
      action: function* go() {
        yield put(push('/services/cashback-info')); // add navigate('/services/cashback-info', { state: { promoType } })
      }
    },
    transactions: {
      depth: 1,
      path: '/transactions',
      action: function* go() {
        yield put(push('/transactions'));
      }
    },
    delete_account: {
      depth: 1,
      path: '/profile/personal-data/delete-account',
      action: function* go() {
        yield put(push('/profile/personal-data/delete-account'));
      }
    },
    mos_login: {
      depth: 1,
      path: '/mos-sso-mobile',
      action: function* go(otherParams) {
        const route = buildRouteWithParams('/mos-sso-mobile', otherParams);
        yield put(push(route));
      }
    },
    wcf: {
      depth: 1,
      path: '/services/wcf',
      action: function* go() {
        yield put(push('/services/wcf'));
      }
    },
  },
}

const normalizePath = (pathname) => {
  if (!pathname || typeof pathname !== 'string') return null

  const pathWithoutQuery = pathname.split(/[?#]/)[0]
  if (pathWithoutQuery === '/') return '/'

  return pathWithoutQuery.replace(/\/+$/, '')
}

export const getSalesforceScreenForPath = (pathname) => {
  const normalizedPath = normalizePath(pathname)
  if (!normalizedPath) return null

  const routeEntry = Object.entries(deepLinkService.routes).find(([, route]) => (
    normalizePath(route.path)?.toLowerCase() === normalizedPath.toLowerCase()
  ))

  if (!routeEntry) return null

  return {
    screenName: normalizePath(routeEntry[1].path),
    deepLinkKey: routeEntry[0].toUpperCase()
  }
}

export default deepLinkService;
